import type { Type } from '@angular/core';
import type { AuroraWidgetDef, AuroraWidgetLink, AuroraWidgetSpan } from '../aurora-widget-def';
import type { WidgetCategory } from '../widget-category';
import type { WidgetDataSource } from '../widget-data-source';
import type { StatFormat } from './stat-widget.component';

// Re-export types so consumers can reach them via @aurora without pulling the component.
export type { StatFormat } from './stat-widget.component';

// ─── StatWidgetConfig ─────────────────────────────────────────────────────────

interface StatWidgetConfig {
  source: WidgetDataSource<number>;
  format?: StatFormat;
  delta?: WidgetDataSource<number>;
  sparkline?: WidgetDataSource<number[]>;
  label?: string;
}

// ─── defineStatWidget ─────────────────────────────────────────────────────────

/** Default span for stat widgets: compact 2×2 tile. */
const STAT_DEFAULT_SPAN: AuroraWidgetSpan = {
  minCols: 2,
  maxCols: 4,
  minRows: 2,
  maxRows: 3,
  defaultCols: 2,
  defaultRows: 2,
};

export interface DefineStatWidgetConfig extends StatWidgetConfig {
  id: string;
  translation: string;
  iconUrl: string;
  description?: string;
  permission?: string | string[];
  category?: WidgetCategory;
  span?: AuroraWidgetSpan;
  link?: AuroraWidgetLink;
}

/**
 * Factory that converts a typed `DefineStatWidgetConfig` into an
 * `AuroraWidgetDef` ready for widget registry registration.
 *
 * The archetype config fields (`source`, `format`, `delta`, `sparkline`,
 * `label`) are placed in `inputs` so the dashboard tile can forward them
 * to `StatWidgetComponent` via `[ngComponentOutletInputs]`.
 *
 * `watermarkIconUrl` is set from `iconUrl` so the dashboard tile paints the
 * associated icon as a translucent full-surface background watermark. This is
 * opt-in per archetype: only stat widgets set it, so chart/table tiles — whose
 * content already fills the surface — never get a watermark.
 */
export function defineStatWidget(config: DefineStatWidgetConfig): AuroraWidgetDef {
  const { id, translation, iconUrl, description, permission, category, span, link } = config;
  const { source, format = 'number', delta, sparkline, label } = config;

  return {
    id,
    translation,
    iconUrl,
    watermarkIconUrl: iconUrl,
    description,
    permission,
    category,
    link,
    span: span ?? STAT_DEFAULT_SPAN,
    inputs: {
      source,
      format,
      ...(delta !== undefined && { delta }),
      ...(sparkline !== undefined && { sparkline }),
      ...(label !== undefined && { label }),
    },
    component: (): Promise<Type<unknown>> =>
      import('./stat-widget.component').then((m) => m.StatWidgetComponent as Type<unknown>),
  };
}
